import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Bell, MessageSquare, ChevronRight, ReceiptText, UtensilsCrossed, RefreshCw, Check, Scissors } from 'lucide-react';
import { apiService } from '@/services/api';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import BranchBroadcasts from '@/components/BranchBroadcasts';
import CallStaff from '@/components/CallStaff/CallStaff';
import BookingChat from '@/components/BookingChat/BookingChat';
import MenuImagesModal from '@/components/MenuImages';
import TableServiceOrderModal from '@/components/TableServiceOrder/TableServiceOrderModal';
import BillModal from '@/components/TableBill';

/**
 * SalonBranchLanding — the public page a salon/spa guest lands on after scanning the BRANCH QR
 * (`/b/:token`). Unlike the table flow, the guest first PICKS A STYLIST; the session then binds to
 * that stylist (not a table) and drops into the same Call staff / Order / Bill / Chat hub.
 */
type Phase = 'loading' | 'pick' | 'ready' | 'error' | 'ended';
interface Stylist { id: string; displayName: string; photoUrl: string | null; specialties: string | null; busy: boolean }

const getDeviceId = (): string => {
  try {
    let id = localStorage.getItem('qrDeviceId');
    if (!id) { id = (window.crypto?.randomUUID?.() || `dev_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`); localStorage.setItem('qrDeviceId', id); }
    return id;
  } catch { return `dev_${Math.random().toString(36).slice(2)}`; }
};

const SalonBranchLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [message, setMessage] = useState('');
  const [branchName, setBranchName] = useState('');
  const [branchId, setBranchId] = useState('');
  const [stylists, setStylists] = useState<Stylist[]>([]);
  const [picking, setPicking] = useState<string | null>(null);

  // Session (after picking a stylist)
  const [bookingId, setBookingId] = useState('');
  const [stylistName, setStylistName] = useState('');
  const [branchType, setBranchType] = useState<{ code?: string; label?: string } | null>(null);
  const [attempt, setAttempt] = useState(0);

  // Appointments (Book ahead)
  const [services, setServices] = useState<Array<{ id: string; name: string; price: number | null; durationMinutes: number | null }>>([]);
  const [mode, setMode] = useState<'walkin' | 'appointment'>('walkin');
  const [apSvc, setApSvc] = useState('');
  const [apStylist, setApStylist] = useState('');
  const [apDate, setApDate] = useState('');
  const [apSlots, setApSlots] = useState<string[]>([]);
  const [apSlot, setApSlot] = useState('');
  const [apName, setApName] = useState('');
  const [apPhone, setApPhone] = useState('');
  const [apLoadingSlots, setApLoadingSlots] = useState(false);
  const [apSubmitting, setApSubmitting] = useState(false);
  const [apDone, setApDone] = useState<null | { time: string; date: string; stylistName: string }>(null);
  const apDuration = useMemo(() => services.find((s) => s.id === apSvc)?.durationMinutes || 30, [services, apSvc]);
  const todayStr = useMemo(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; }, []);

  const [showCall, setShowCall] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showOrder, setShowOrder] = useState(false);
  const [showBill, setShowBill] = useState(false);

  // Lock background scroll on this app-like screen.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    if (!token) { setPhase('error'); setMessage('Invalid QR code.'); return; }
    let cancelled = false;
    (async () => {
      setPhase('loading');
      try {
        const res = await apiService.getBranchStylists(token);
        if (cancelled) return;
        const d = res?.data || {};
        setBranchName(d.branchName || '');
        if (d.branchId) setBranchId(d.branchId);
        setStylists((d.stylists || []) as Stylist[]);
        setServices((d.services || []) as any);
        setPhase('pick');
      } catch (e) {
        if (cancelled) return;
        setPhase('error');
        setMessage(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
      }
    })();
    return () => { cancelled = true; };
  }, [token, attempt]);

  const pickStylist = async (s: Stylist) => {
    if (picking || s.busy || !token) return;
    setPicking(s.id);
    try {
      const res = await apiService.createSalonSession(token, { stylistId: s.id, deviceId: getDeviceId() });
      const d = res?.data || {};
      if (!d.accessToken || !d.bookingId) throw new Error('Could not start your session.');
      apiService.setGuestTokens(d.accessToken, d.refreshToken); // tab-scoped: see api.ts getTokens()
      setBookingId(d.bookingId);
      if (d.branchId) setBranchId(d.branchId);
      setStylistName(d.stylistName || s.displayName);
      if (d.branchType) setBranchType({ code: d.branchType.code, label: d.branchType.label });
      setPhase('ready');
    } catch (e) {
      const code = (e as any)?.code;
      if (code === 'QR_SESSION_ENDED' || (e as any)?.status === 410) { setPhase('ended'); setMessage(e instanceof Error ? e.message : 'Your session has ended.'); }
      else { setMessage(e instanceof Error ? e.message : 'Could not start your session. Please try again.'); }
    } finally {
      setPicking(null);
    }
  };

  const retry = () => setAttempt((a) => a + 1);

  // Fetch free slots when a stylist + date (+ service duration) are chosen.
  useEffect(() => {
    if (mode !== 'appointment' || !apStylist || !apDate || !token) { setApSlots([]); return; }
    let alive = true;
    setApLoadingSlots(true); setApSlot('');
    apiService.getSalonAvailability(token, apStylist, apDate, apDuration)
      .then((r) => { if (alive) setApSlots(r?.data?.slots || []); })
      .catch(() => { if (alive) setApSlots([]); })
      .finally(() => { if (alive) setApLoadingSlots(false); });
    return () => { alive = false; };
  }, [mode, apStylist, apDate, apDuration, token]);

  const submitAppt = async () => {
    if (!apStylist || !apDate || !apSlot || !apName.trim() || apSubmitting || !token) return;
    setApSubmitting(true);
    try {
      const res = await apiService.createSalonAppointment(token, { stylistId: apStylist, serviceItemId: apSvc || undefined, date: apDate, time: apSlot, name: apName.trim(), phone: apPhone.trim() || undefined });
      const d = res?.data || {};
      setApDone({ time: d.time || apSlot, date: d.date || apDate, stylistName: d.stylistName || '' });
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Could not book. Please try again.');
    } finally {
      setApSubmitting(false);
    }
  };

  const hero = useMemo(() => (
    <div style={styles.hero}>
      <div style={styles.heroLogo}><Scissors size={24} color="#fff" /></div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={styles.heroName}>{branchName || 'Salon'}</div>
        {phase === 'ready' && stylistName && <div style={styles.tablePill}>✂️ With {stylistName}</div>}
        {phase === 'pick' && <div style={styles.heroSub}>Choose your stylist to get started</div>}
      </div>
    </div>
  ), [branchName, phase, stylistName]);

  return (
    <div style={styles.page}>
      <ErrorBoundary>
        {phase === 'loading' ? (
          <div style={styles.splash}>
            <div style={styles.splashLogoWrap}><div style={styles.splashRing} aria-hidden /><div style={styles.splashLogo}><Scissors size={32} color="#fff" /></div></div>
            <div style={styles.splashBrand}>{branchName || 'Welcome'}</div>
            <div style={styles.splashMuted}>Loading stylists…</div>
          </div>
        ) : (
          <div style={styles.shell}>
            {hero}
            <div style={{ padding: '0 16px' }}><BranchBroadcasts branchId={branchId || undefined} /></div>
            <div style={styles.body}>
              {phase === 'error' && (
                <div style={styles.center}>
                  <div style={styles.stateIcon}><RefreshCw size={28} color="#ef4444" /></div>
                  <div style={styles.title}>Couldn’t load</div>
                  <div style={styles.muted}>{message}</div>
                  <button style={styles.retryBtn} onClick={retry}><RefreshCw size={15} /> Try again</button>
                </div>
              )}
              {phase === 'ended' && (
                <div style={styles.center}>
                  <div style={{ ...styles.stateIcon, background: 'rgba(34,197,94,0.14)' }}><Check size={28} color="#22c55e" /></div>
                  <div style={styles.title}>You’re checked out</div>
                  <div style={styles.muted}>{message || 'This session has ended. Scan the QR again to start a new one.'}</div>
                </div>
              )}
              {phase === 'pick' && (
                <>
                  {message && <div style={styles.errorBox}>{message}</div>}
                  <div style={styles.modeTabs}>
                    <button type="button" style={{ ...styles.modeTab, ...(mode === 'walkin' ? styles.modeTabOn : {}) }} onClick={() => setMode('walkin')}>Walk in now</button>
                    <button type="button" style={{ ...styles.modeTab, ...(mode === 'appointment' ? styles.modeTabOn : {}) }} onClick={() => setMode('appointment')}>Book ahead</button>
                  </div>

                  {mode === 'walkin' ? (
                    stylists.length === 0 ? (
                      <div style={styles.muted}>No stylists available right now — please ask at reception.</div>
                    ) : (
                      <div style={styles.stylistGrid}>
                        {stylists.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            style={{ ...styles.stylistCard, ...(s.busy ? styles.stylistBusy : {}), ...(picking === s.id ? { opacity: 0.6 } : {}) }}
                            disabled={s.busy || !!picking}
                            onClick={() => pickStylist(s)}
                          >
                            <div style={styles.stylistAvatar}>
                              {s.photoUrl ? <img src={s.photoUrl} alt={s.displayName} style={styles.stylistImg} /> : <span style={{ fontSize: 26 }}>{s.displayName.trim().charAt(0).toUpperCase()}</span>}
                            </div>
                            <div style={styles.stylistName}>{picking === s.id ? 'Starting…' : s.displayName}</div>
                            {s.specialties && <div style={styles.stylistSpec}>{s.specialties}</div>}
                            <div style={{ ...styles.availPill, ...(s.busy ? styles.availBusy : styles.availFree) }}>{s.busy ? 'Busy' : 'Available'}</div>
                          </button>
                        ))}
                      </div>
                    )
                  ) : apDone ? (
                    <div style={styles.center}>
                      <div style={{ ...styles.stateIcon, background: 'rgba(34,197,94,0.14)' }}><Check size={28} color="#22c55e" /></div>
                      <div style={styles.title}>Appointment requested 🎉</div>
                      <div style={styles.muted}>{apDone.stylistName ? `${apDone.stylistName} · ` : ''}{apDone.date} at {apDone.time}. The salon will confirm shortly.</div>
                      <button style={styles.retryBtn} onClick={() => { setApDone(null); setApSlot(''); }}>Book another</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {services.length > 0 && (
                        <label style={styles.fieldLabel}>Service
                          <select style={styles.select} value={apSvc} onChange={(e) => setApSvc(e.target.value)}>
                            <option value="">Any / not sure</option>
                            {services.map((s) => <option key={s.id} value={s.id}>{s.name}{s.durationMinutes ? ` · ${s.durationMinutes} min` : ''}</option>)}
                          </select>
                        </label>
                      )}
                      <label style={styles.fieldLabel}>Stylist
                        <select style={styles.select} value={apStylist} onChange={(e) => setApStylist(e.target.value)}>
                          <option value="">Choose a stylist</option>
                          {stylists.map((s) => <option key={s.id} value={s.id}>{s.displayName}</option>)}
                        </select>
                      </label>
                      <label style={styles.fieldLabel}>Date
                        <input type="date" min={todayStr} style={styles.select} value={apDate} onChange={(e) => setApDate(e.target.value)} />
                      </label>
                      {apStylist && apDate && (
                        <div>
                          <div style={styles.fieldLabel}>Time</div>
                          {apLoadingSlots ? <div style={styles.muted}>Loading times…</div>
                            : apSlots.length === 0 ? <div style={styles.muted}>No free slots that day — try another date.</div>
                            : <div style={styles.slotGrid}>{apSlots.map((t) => <button key={t} type="button" style={{ ...styles.slot, ...(apSlot === t ? styles.slotOn : {}) }} onClick={() => setApSlot(t)}>{t}</button>)}</div>}
                        </div>
                      )}
                      {apSlot && (
                        <>
                          <input style={styles.select} placeholder="Your name" value={apName} maxLength={100} onChange={(e) => setApName(e.target.value)} />
                          <input style={styles.select} placeholder="Phone (optional)" value={apPhone} maxLength={20} onChange={(e) => setApPhone(e.target.value)} />
                          <button type="button" style={{ ...styles.bookBtn, opacity: apName.trim() && !apSubmitting ? 1 : 0.6 }} disabled={!apName.trim() || apSubmitting} onClick={submitAppt}>
                            {apSubmitting ? 'Booking…' : 'Book appointment'}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
              {phase === 'ready' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={styles.sectionLabel}>What would you like to do?</div>
                  <button style={styles.callBtn} onClick={() => setShowCall(true)}>
                    <span style={styles.callIcon}><Bell size={22} color="#fff" /></span>
                    <span style={{ flex: 1, textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={styles.callTitle}>Call your stylist</span>
                      <span style={styles.callSub}>Get help at your chair</span>
                    </span>
                  </button>
                  {branchId && bookingId && (
                    <button style={styles.actionRow} onClick={() => setShowOrder(true)}>
                      <span style={styles.actionIcon}><UtensilsCrossed size={20} color="#FF6B35" /></span>
                      <span style={styles.actionText}><span style={styles.actionTitle}>Services & add-ons</span><span style={styles.actionSub}>Browse & request</span></span>
                      <ChevronRight size={18} color="#cbd5e1" />
                    </button>
                  )}
                  {bookingId && (
                    <button style={styles.actionRow} onClick={() => setShowBill(true)}>
                      <span style={styles.actionIcon}><ReceiptText size={20} color="#FF6B35" /></span>
                      <span style={styles.actionText}><span style={styles.actionTitle}>View bill / Pay</span><span style={styles.actionSub}>See charges & pay online</span></span>
                      <ChevronRight size={18} color="#cbd5e1" />
                    </button>
                  )}
                  <button style={styles.actionRow} onClick={() => setShowChat(true)}>
                    <span style={styles.actionIcon}><MessageSquare size={20} color="#FF6B35" /></span>
                    <span style={styles.actionText}><span style={styles.actionTitle}>Chat</span><span style={styles.actionSub}>Message the team</span></span>
                    <ChevronRight size={18} color="#cbd5e1" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {phase === 'ready' && showCall && bookingId && (
          <CallStaff bookingId={bookingId} tableName={stylistName} branchType={branchType} branchId={branchId} onClose={() => setShowCall(false)} />
        )}
        {phase === 'ready' && showChat && bookingId && (
          <BookingChat bookingId={bookingId} organizerName={branchName || 'Staff'} branchName={branchName || ''} branchId={branchId || undefined} onClose={() => setShowChat(false)} />
        )}
        {phase === 'ready' && showOrder && branchId && bookingId && (
          <TableServiceOrderModal branchId={branchId} bookingId={bookingId} tableName={stylistName} onClose={() => setShowOrder(false)} onViewMenu={() => setShowMenu(true)} />
        )}
        {phase === 'ready' && showBill && bookingId && (
          <BillModal bookingId={bookingId} tableName={stylistName} onClose={() => setShowBill(false)} />
        )}
        {phase === 'ready' && showMenu && branchId && (
          <MenuImagesModal branchId={branchId} branchName={branchName || stylistName} onClose={() => setShowMenu(false)} />
        )}
      </ErrorBoundary>
      <style>{`@keyframes cuPulse{0%{transform:scale(1);opacity:0.5}100%{transform:scale(1.6);opacity:0}}@keyframes cuFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}`}</style>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100dvh', background: '#eef1f5', display: 'flex', justifyContent: 'center', boxSizing: 'border-box' },
  shell: { width: 'min(480px, 100%)', minHeight: '100dvh', background: '#ffffff', display: 'flex', flexDirection: 'column', boxShadow: '0 0 40px rgba(15,23,42,0.06)' },
  splash: { width: 'min(480px, 100%)', minHeight: '100dvh', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24, boxSizing: 'border-box' },
  splashLogoWrap: { position: 'relative', width: 96, height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  splashRing: { position: 'absolute', width: 84, height: 84, borderRadius: 26, background: 'rgba(255,107,53,0.28)', animation: 'cuPulse 1.6s ease-out infinite' },
  splashLogo: { position: 'relative', width: 84, height: 84, borderRadius: 26, background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 14px 34px rgba(255,107,53,0.4)', animation: 'cuFloat 2.6s ease-in-out infinite' },
  splashBrand: { fontSize: '1.2rem', fontWeight: 800, color: '#0f172a' },
  splashMuted: { color: '#94a3b8', fontSize: '0.9rem' },
  hero: { display: 'flex', alignItems: 'center', gap: 14, padding: 'calc(20px + env(safe-area-inset-top)) 18px 18px', background: 'linear-gradient(180deg,#FFF4EE 0%,#ffffff 100%)', borderBottom: '1px solid #f1ece8' },
  heroLogo: { width: 58, height: 58, borderRadius: 16, background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 8px 20px rgba(255,107,53,0.32)' },
  heroName: { fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.15, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  heroSub: { fontSize: '0.85rem', color: '#64748b', marginTop: 3 },
  tablePill: { display: 'inline-block', marginTop: 7, background: 'rgba(255,107,53,0.12)', color: '#e0531f', fontWeight: 800, fontSize: '0.9rem', padding: '4px 12px', borderRadius: 9999, border: '1px solid rgba(255,107,53,0.28)' },
  body: { flex: 1, padding: '16px 16px calc(20px + env(safe-area-inset-bottom))', overflowY: 'auto' },
  sectionLabel: { fontSize: '0.8rem', fontWeight: 700, letterSpacing: 0.3, color: '#94a3b8', textTransform: 'uppercase', margin: '4px 2px 10px' },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', padding: '40px 12px' },
  stateIcon: { width: 60, height: 60, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' },
  muted: { color: '#64748b', fontSize: '0.95rem', lineHeight: 1.45 },
  errorBox: { background: '#fef2f2', color: '#b91c1c', borderRadius: 10, padding: '8px 12px', fontSize: '0.85rem', marginBottom: 10 },
  retryBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', color: '#0f172a', border: '1px solid #e2e8f0', borderRadius: 12, padding: '11px 18px', fontWeight: 700, cursor: 'pointer', marginTop: 6 },
  stylistGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
  stylistCard: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #eef1f5', borderRadius: 16, padding: '16px 10px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(15,23,42,0.05)' },
  stylistBusy: { opacity: 0.55, cursor: 'not-allowed' },
  stylistAvatar: { width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#FF8A5B,#FFC59B)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, overflow: 'hidden' },
  stylistImg: { width: '100%', height: '100%', objectFit: 'cover' },
  stylistName: { fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', textAlign: 'center' },
  stylistSpec: { fontSize: '0.76rem', color: '#94a3b8', textAlign: 'center', lineHeight: 1.25 },
  availPill: { fontSize: '0.72rem', fontWeight: 800, borderRadius: 9999, padding: '2px 10px', marginTop: 2 },
  availFree: { background: '#ecfdf5', color: '#059669', border: '1px solid #6ee7b7' },
  availBusy: { background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb' },
  callBtn: { position: 'relative', display: 'flex', alignItems: 'center', gap: 14, background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: 16, padding: '16px 18px', cursor: 'pointer', boxShadow: '0 10px 24px rgba(255,107,53,0.34)', textAlign: 'left', width: '100%' },
  callIcon: { width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  callTitle: { fontSize: '1.1rem', fontWeight: 800, lineHeight: 1.1 },
  callSub: { fontSize: '0.82rem', fontWeight: 500, opacity: 0.92 },
  actionRow: { display: 'flex', alignItems: 'center', gap: 13, background: '#ffffff', color: '#0f172a', border: '1px solid #eef1f5', borderRadius: 16, padding: '13px 14px', cursor: 'pointer', width: '100%', textAlign: 'left', boxShadow: '0 2px 8px rgba(15,23,42,0.04)' },
  actionIcon: { width: 42, height: 42, borderRadius: 12, background: 'rgba(255,107,53,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  actionText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  actionTitle: { fontSize: '0.98rem', fontWeight: 700, color: '#0f172a' },
  actionSub: { fontSize: '0.8rem', color: '#94a3b8' },
  modeTabs: { display: 'flex', gap: 6, background: '#f1f5f9', borderRadius: 12, padding: 4, marginBottom: 14 },
  modeTab: { flex: 1, border: 'none', background: 'transparent', color: '#64748b', fontWeight: 700, fontSize: '0.88rem', padding: '9px', borderRadius: 9, cursor: 'pointer' },
  modeTabOn: { background: '#fff', color: '#e0531f', boxShadow: '0 1px 4px rgba(15,23,42,0.08)' },
  fieldLabel: { display: 'flex', flexDirection: 'column', gap: 5, fontSize: '0.8rem', fontWeight: 700, color: '#64748b' },
  select: { border: '1px solid #e2e8f0', borderRadius: 12, padding: '11px 12px', fontSize: '0.92rem', color: '#0f172a', background: '#fff', outline: 'none', width: '100%', boxSizing: 'border-box', fontWeight: 500 },
  slotGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 6 },
  slot: { border: '1px solid #e2e8f0', background: '#fff', color: '#0f172a', borderRadius: 10, padding: '9px 4px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' },
  slotOn: { background: 'rgba(255,107,53,0.12)', color: '#e0531f', borderColor: 'rgba(255,107,53,0.5)' },
  bookBtn: { background: 'linear-gradient(135deg,#FF6B35,#FF8A5B)', color: '#fff', border: 'none', borderRadius: 14, padding: '14px', fontWeight: 800, fontSize: '1rem', cursor: 'pointer', boxShadow: '0 10px 24px rgba(255,107,53,0.34)' },
};

export default SalonBranchLanding;
